import get from 'lodash/get';
import merge from 'lodash/merge';
import map from 'lodash/map';
import config from '../../config';
import RestEntityAPI from './restEntityAPI';
import logger from '../logger';
import fetch from '../fetch';

const options = {
  entityName: 'profile',
  endpoint: () => `${config.apiUrl}/v1/profiles`,
  searchEndpoint: `${config.apiUrl}/v1/profile-search`,
};

const ProfilesAPI = () => {
  const parentAPI = RestEntityAPI(options);

  const searchEntities = async (query) => {
    const response = await parentAPI.searchEntities(query);
    // We are not returning the full response because different entities
    // have different response structures for search. So we only return the
    // list of entities.
    return { data: get(response, 'data.content', []) };
  };

  const getEntitiesCompact = async (data) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/compact`, {
        body: data,
        method: 'POST',
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while loading ${options.entityName}s`, error);
      throw error;
    }
  };

  const updateEntities = async (query, params) => {
    try {
      const encodedParams = map(params, param => encodeURIComponent(param));
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}${parentAPI.toQueryString(encodedParams)}`, {
        method: 'PATCH',
        body: query,
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while updating ${options.entityName}s`, error);
      throw error;
    }
  };

  const getEntitiesByUsername = async (username, fetchOptions = {}) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/${username}/compact/by-username`, {
        ...fetchOptions,
        method: 'GET',
      });

      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while loading ${options.entityName}s`, error);
      throw error;
    }
  };

  // TODO EMPD-2516
  const getUnauthenticatedAttributes = async (profileKey, fetchOptions = {}) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/${profileKey}/SOMETHING`, {
        ...fetchOptions,
        method: 'GET',
      });

      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while fetching ${options.entityName}s unauthenticated attributes`, error);
      throw error;
    }
  };

  const getFreeFormLocationByArrival = async (fetchOptions = {}) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/location`, {
        ...fetchOptions,
        method: 'GET',
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while fetching ${options.entityName}s location data`, error);
      throw error;
    }
  };

  const getEntitiesByEmail = async (data, fetchOptions = {}) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/by-email`, {
        ...fetchOptions,
        method: 'POST',
        body: data,
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while fetching ${options.entityName}s profileKeys by emails`, error);
      throw error;
    }
  };

  const getProfileJobsByCompanyName = async (query = {}) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/profile-company-name${parentAPI.toQueryString(query)}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      logger.logDebug(`Encountered error while fetching ${options.entityName}s company jobs data`, error);
      throw error;
    }
  };

  const saveContacts = async (data, fetchOptions = {}) => {
    try {
      await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/upload-contacts`, {
        ...fetchOptions,
        method: 'POST',
        body: data,
      });
    } catch (error) {
      logger.logError('Encountered error while uploading contacts to profile', error);
      throw error;
    }
  };

  const getProfileByKey = async (profileKey) => {
    try {
      const response = await fetch(`${parentAPI.buildEndpoint(options.endpoint)}/${profileKey}`, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      logger.logDebug('Encountered error while get profile', error);
      throw error;
    }
  };

  // Same public endpoint the jobcase.com password-recover page uses. Always answers 204
  // (anti-enumeration), so success only means the request was accepted - not that an
  // email was deliverable for that account.
  const sendPasswordResetEmail = async (email) => {
    try {
      return await fetch(`${config.apiUrl}/v1/my-profile/reset-password-email`, {
        method: 'PUT',
        body: { email },
      });
    } catch (error) {
      logger.logDebug('Encountered error while requesting a password reset email', error);
      throw error;
    }
  };

  return merge({}, { ...parentAPI },
    {
      searchEntities,
      getEntitiesCompact,
      updateEntities,
      getEntitiesByUsername,
      getUnauthenticatedAttributes,
      getFreeFormLocationByArrival,
      getProfileJobsByCompanyName,
      getEntitiesByEmail,
      saveContacts,
      getProfileByKey,
      sendPasswordResetEmail,
    });
};

export default ProfilesAPI;
